import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { createElement, type ReactNode } from 'react';
import { BRAND } from '@/constants/brand';

type ConfirmTheme = 'light' | 'dark';
type ConfirmVariant = 'primary' | 'danger';

const cancelButtonStyle = {
  borderColor: BRAND.inputBorder,
  color: BRAND.heading,
};

const primaryButtonStyle = {
  background: BRAND.gradientButton,
  borderColor: BRAND.gradientButton,
  boxShadow: BRAND.shadow,
};

const dangerButtonStyle = {
  background: BRAND.redButton,
  borderColor: BRAND.redButton,
};

function resolveConfirmTheme(theme?: ConfirmTheme): ConfirmTheme {
  if (theme) return theme;
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

function applyConfirmTheme(theme: ConfirmTheme) {
  document.body.dataset.confirmTheme = theme;
}

function clearConfirmTheme() {
  delete document.body.dataset.confirmTheme;
}

function getConfirmIcon(variant: ConfirmVariant) {
  return createElement(ExclamationCircleOutlined, {
    className: `betiyan-confirm-icon betiyan-confirm-icon--${variant}`,
    style: { color: variant === 'danger' ? BRAND.redButton : BRAND.primary },
  });
}

function wrapConfirmMessage(message: ReactNode) {
  return createElement(
    'div',
    { className: 'betiyan-confirm-content' },
    createElement('p', { className: 'betiyan-confirm-message' }, message),
  );
}

interface OpenConfirmModalOptions {
  title: string;
  content: ReactNode;
  okText: string;
  cancelText?: string;
  variant: ConfirmVariant;
  theme?: ConfirmTheme;
}

function openConfirmModal({
  title,
  content,
  okText,
  cancelText = 'Cancel',
  variant,
  theme,
}: OpenConfirmModalOptions): Promise<boolean> {
  const resolvedTheme = resolveConfirmTheme(theme);
  applyConfirmTheme(resolvedTheme);

  return new Promise((resolve) => {
    Modal.confirm({
      title,
      icon: getConfirmIcon(variant),
      content,
      okText,
      cancelText,
      centered: true,
      className: `betiyan-confirm-modal betiyan-confirm-modal--${variant}`,
      rootClassName: `betiyan-confirm-modal-root betiyan-confirm-modal-root--${variant}`,
      okButtonProps: {
        danger: variant === 'danger',
        className: 'betiyan-confirm-ok-btn',
        style: variant === 'danger' ? dangerButtonStyle : primaryButtonStyle,
      },
      cancelButtonProps: {
        className: 'betiyan-confirm-cancel-btn',
        style: cancelButtonStyle,
      },
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
      afterClose: clearConfirmTheme,
    });
  });
}

interface StatusTransitionContentProps {
  fieldLabel: string;
  previousLabel: string;
  nextLabel: string;
}

function StatusTransitionContent({
  fieldLabel,
  previousLabel,
  nextLabel,
}: StatusTransitionContentProps) {
  return createElement(
    'div',
    { className: 'betiyan-confirm-transitionBlock' },
    createElement('span', { className: 'betiyan-confirm-transitionLabel' }, fieldLabel),
    createElement(
      'div',
      { className: 'betiyan-confirm-statusChange' },
      createElement('span', { className: 'betiyan-confirm-statusFrom' }, previousLabel),
      createElement('span', { className: 'betiyan-confirm-statusArrow', 'aria-hidden': true }, '→'),
      createElement('span', { className: 'betiyan-confirm-statusTo' }, nextLabel),
    ),
  );
}

interface OrderStatusConfirmContentProps {
  previousLabel: string;
  nextLabel: string;
  emailTarget?: string;
}

function OrderStatusConfirmContent({
  previousLabel,
  nextLabel,
  emailTarget,
}: OrderStatusConfirmContentProps) {
  return createElement(
    'div',
    { className: 'betiyan-confirm-content' },
    createElement(StatusTransitionContent, {
      fieldLabel: 'Order status',
      previousLabel,
      nextLabel,
    }),
    createElement(
      'p',
      { className: 'betiyan-confirm-message' },
      emailTarget
        ? `A confirmation email will be sent to ${emailTarget}.`
        : 'No customer email is on file, so no notification will be sent.',
    ),
  );
}

interface PaymentStatusConfirmContentProps {
  previousLabel: string;
  nextLabel: string;
}

function PaymentStatusConfirmContent({
  previousLabel,
  nextLabel,
}: PaymentStatusConfirmContentProps) {
  return createElement(
    'div',
    { className: 'betiyan-confirm-content' },
    createElement(StatusTransitionContent, {
      fieldLabel: 'Payment status',
      previousLabel,
      nextLabel,
    }),
    createElement(
      'p',
      { className: 'betiyan-confirm-message' },
      'This will update the payment status for this order.',
    ),
  );
}

interface OrderUpdatesConfirmContentProps {
  orderStatus?: StatusTransitionContentProps;
  paymentStatus?: StatusTransitionContentProps;
  emailTarget?: string;
}

function OrderUpdatesConfirmContent({
  orderStatus,
  paymentStatus,
  emailTarget,
}: OrderUpdatesConfirmContentProps) {
  return createElement(
    'div',
    { className: 'betiyan-confirm-content' },
    orderStatus ? createElement(StatusTransitionContent, orderStatus) : null,
    paymentStatus ? createElement(StatusTransitionContent, paymentStatus) : null,
    orderStatus
      ? createElement(
          'p',
          { className: 'betiyan-confirm-message' },
          emailTarget
            ? `A confirmation email will be sent to ${emailTarget}.`
            : 'No customer email is on file, so no notification will be sent.',
        )
      : createElement(
          'p',
          { className: 'betiyan-confirm-message' },
          'This will update the selected statuses for this order.',
        ),
  );
}

interface OrderCancelConfirmContentProps {
  emailTarget?: string;
}

function OrderCancelConfirmContent({ emailTarget }: OrderCancelConfirmContentProps) {
  return createElement(
    'div',
    { className: 'betiyan-confirm-content' },
    createElement(
      'p',
      { className: 'betiyan-confirm-message' },
      'Inventory stock will be restored for all active items in this order.',
    ),
    createElement(
      'p',
      { className: 'betiyan-confirm-note' },
      emailTarget
        ? `A cancellation email will be sent to ${emailTarget}.`
        : 'No customer email is on file, so no notification will be sent.',
    ),
  );
}

export function confirmOrderStatusUpdate(
  input: OrderStatusConfirmContentProps,
  theme?: ConfirmTheme,
): Promise<boolean> {
  return openConfirmModal({
    title: 'Update order status?',
    content: createElement(OrderStatusConfirmContent, input),
    okText: 'Update status',
    cancelText: 'Keep current status',
    variant: 'primary',
    theme,
  });
}

export function confirmPaymentStatusUpdate(
  input: PaymentStatusConfirmContentProps,
  theme?: ConfirmTheme,
): Promise<boolean> {
  return openConfirmModal({
    title: 'Update payment status?',
    content: createElement(PaymentStatusConfirmContent, input),
    okText: 'Update status',
    cancelText: 'Keep current status',
    variant: 'primary',
    theme,
  });
}

export function confirmOrderUpdates(
  input: OrderUpdatesConfirmContentProps,
  theme?: ConfirmTheme,
): Promise<boolean> {
  return openConfirmModal({
    title: 'Update order?',
    content: createElement(OrderUpdatesConfirmContent, input),
    okText: 'Update status',
    cancelText: 'Keep current status',
    variant: 'primary',
    theme,
  });
}

export function confirmOrderCancel(
  input: OrderCancelConfirmContentProps,
  theme?: ConfirmTheme,
): Promise<boolean> {
  return openConfirmModal({
    title: 'Cancel this order?',
    content: createElement(OrderCancelConfirmContent, input),
    okText: 'Cancel order',
    cancelText: 'Keep order',
    variant: 'danger',
    theme,
  });
}

export function confirmLogout(theme?: ConfirmTheme): Promise<boolean> {
  return openConfirmModal({
    title: 'Sign out?',
    content: wrapConfirmMessage('Are you sure you want to sign out of your account?'),
    okText: 'Sign out',
    variant: 'danger',
    theme,
  });
}

export function confirmDelete(itemLabel: string, theme?: ConfirmTheme): Promise<boolean> {
  return openConfirmModal({
    title: `Delete ${itemLabel}?`,
    content: wrapConfirmMessage('This action cannot be undone.'),
    okText: 'Delete',
    variant: 'danger',
    theme,
  });
}

export function confirmPermanentLocationDelete(
  itemLabel: string,
  childDescription?: string,
  theme?: ConfirmTheme,
): Promise<boolean> {
  const childWarning = childDescription ? ` ${childDescription} will also be permanently deleted.` : '';
  return openConfirmModal({
    title: `Permanently delete ${itemLabel}?`,
    content: wrapConfirmMessage(
      `This action cannot be undone.${childWarning} Deletion is blocked if linked products exist.`,
    ),
    okText: 'Delete permanently',
    variant: 'danger',
    theme,
  });
}

export function confirmPermanentProductDelete(
  itemLabel: string,
  theme?: ConfirmTheme,
): Promise<boolean> {
  return openConfirmModal({
    title: `Permanently delete ${itemLabel}?`,
    content: wrapConfirmMessage(
      'This action cannot be undone. Product variants, inventory, reviews, and cart entries will also be permanently deleted.',
    ),
    okText: 'Delete permanently',
    variant: 'danger',
    theme,
  });
}

export function confirmPermanentProductBulkDelete(
  count: number,
  theme?: ConfirmTheme,
): Promise<boolean> {
  return openConfirmModal({
    title: `Permanently delete ${count} selected product${count === 1 ? '' : 's'}?`,
    content: wrapConfirmMessage(
      'This action cannot be undone. Related variants, inventory, reviews, and cart entries will also be permanently deleted.',
    ),
    okText: 'Delete permanently',
    variant: 'danger',
    theme,
  });
}

export function confirmBulkDelete(count: number, theme?: ConfirmTheme): Promise<boolean> {
  return openConfirmModal({
    title: `Delete ${count} selected item${count === 1 ? '' : 's'}?`,
    content: wrapConfirmMessage('This action cannot be undone.'),
    okText: 'Delete',
    variant: 'danger',
    theme,
  });
}
